import { TranslocoService } from '@ngneat/transloco';
import { LearnerFormUpdatePermission } from 'src/app/page-modules/resource/models';
import { EditorContent, ReviewEventType, UserPlaylistSubmissionSummary } from '../models';

export class ContentHelper {
  static isFrameMode(): boolean {
    return window.self !== window.top;
  }

  /**
   * Extracts a plain-text summary from an EditorContent[] block list, joining non-empty paragraphs. Paragraphs are
   * stored escaped (`'` as `&#39;`), so entities are decoded too - an inert parse, so no markup in them can run.
   */
  static extractPlainText(content: EditorContent[] | null | undefined): string {
    if (!Array.isArray(content)) return '';
    const parser = new DOMParser();
    return content
      .filter((block) => block.type === 'PARAGRAPH' && typeof block.content === 'string')
      .map((block) => {
        const text = (block.content as string).replace(/<[^>]*>/g, ' ');
        return (parser.parseFromString(text, 'text/html').body.textContent ?? '').trim();
      })
      .filter((text) => text.length > 0)
      .join('\n');
  }

  /** Whether an EditorContent[] block list has any real content: non-empty paragraph text, or any non-paragraph block. */
  static hasNonEmptyContent(content: EditorContent[] | null | undefined): boolean {
    if (!Array.isArray(content)) return false;
    return content.some((block) => {
      if (block.type !== 'PARAGRAPH') return true;
      const text = typeof block.content === 'string' ? block.content.replace(/<[^>]*>/g, '').trim() : '';
      return text.length > 0;
    });
  }

  static convertTimeEstimate(timeInMinutes: number): string {
    const hours = Math.floor(timeInMinutes / 60);
    const minutes = timeInMinutes % 60;
    if (hours === 0 && minutes === 0) {
      return '';
    }
    return hours ? `${hours}h ${minutes} min` : `${minutes} min`;
  }

  static getOrgDiscoveryUrl() {
    return localStorage.getItem('orgDiscoveryUrl') ?? 'discovery';
  }

  static setOrgDiscoveryUrl(url?: string) {
    url = url ? url : 'discovery';
    return localStorage.setItem('orgDiscoveryUrl', `/${url}`);
  }

  // TODO: replace with learnerFormAnswer.updatePermission when switching to new forms
  // return true if form can be submitted
  static checkFormSubmissionStatus(userPlaylistSubmissionSummary: UserPlaylistSubmissionSummary): boolean {
    let canReSubmit = false;
    if (userPlaylistSubmissionSummary && userPlaylistSubmissionSummary.submissionMode === 'SINGLE') {
      if (userPlaylistSubmissionSummary.submittedOn === null) {
        canReSubmit = true;
        return canReSubmit;
      }
      if (
        userPlaylistSubmissionSummary.submittedOn &&
        userPlaylistSubmissionSummary.userReviewSummary?.status?.currentReviewEvent?.type === ReviewEventType.REQUIRES_RESUBMISSION
      ) {
        canReSubmit = true;
        return canReSubmit;
      }
    }
    return canReSubmit;
  }

  static formCanNotBeEditedReason(translocoService: TranslocoService, formUpdatePermission: LearnerFormUpdatePermission): string {
    if (formUpdatePermission?.canBeUpdated === false) {
      switch (formUpdatePermission.notPermittedReason) {
        case 'REVIEW_OPEN':
          return translocoService.translate('translations.formUpdateDisabledReasons.reviewOpen');
        case 'REVIEW_CLOSED':
          return translocoService.translate('translations.formUpdateDisabledReasons.reviewClosed');
        case 'DEADLINE_PASSED':
          return translocoService.translate('translations.formUpdateDisabledReasons.deadlinePassed');
        case 'FORM_ALREADY_SUBMITTED':
          return translocoService.translate('translations.formUpdateDisabledReasons.formAlreadySubmitted');
        case 'PLAYLIST_ALREADY_SUBMITTED':
          return translocoService.translate('translations.formUpdateDisabledReasons.playlistAlreadySubmitted');
        default:
          return '';
      }
    } else {
      return '';
    }
  }

  static shouldDisplayReviewersDialogOnFormSubmission(userPlaylistSubmissionSummary: UserPlaylistSubmissionSummary): boolean {
    if (this.isProgressiveSubmissionMode(userPlaylistSubmissionSummary)) {
      const reviewSummary = userPlaylistSubmissionSummary?.userReviewSummary;
      if (
        reviewSummary &&
        reviewSummary.reviewType !== 'AUTOMATIC' &&
        !reviewSummary.areReviewersFixed &&
        (!reviewSummary.status || !reviewSummary.status.open || reviewSummary.canSelectNewReviewers)
      ) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  static isProgressiveSubmissionMode(userPlaylistSubmissionSummary: UserPlaylistSubmissionSummary): boolean {
    return userPlaylistSubmissionSummary?.submissionMode === 'PROGRESSIVE';
  }

  static stripHtmlTags(htmlContent: string): string {
    return htmlContent.replace(/<[^>]*>?|\s+/gm, ' ');
  }

  static stripOnlyHtmlTags(htmlContent: string): string {
    return htmlContent.replace(/<[^>]*>/g, ' '); // Removes HTML tags
  }

  /**
   * Counts the words of a textbox form answer exactly the way yuna-api does when it enforces the word limit
   * (`LearnerFormUpdateValidator.countAnswerWords`): every tag and every `&nbsp;` is a separator, nothing else is.
   * The learner sees this number next to the limit while typing and the API rejects answers over the limit using
   * the same count, so the two implementations must stay identical - including the whitespace class, because JS
   * `\s` matches Unicode spaces that Java's `\s` does not.
   */
  static countFormAnswerWords(answer: string | null | undefined): number {
    return ContentHelper.countPlainWords((answer ?? '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;|&#160;|&#xa0;/gi, ' '));
  }

  /**
   * Drops everything after the first `maxWords` words of a textbox answer (counted like `countFormAnswerWords`)
   * while keeping the surrounding markup intact. Because every tag is a separator a word never spans two text
   * nodes, so the cut always lands inside a single text node; whatever follows it is removed along with any
   * element that is left empty.
   */
  static truncateFormAnswerToWords(answer: string | null | undefined, maxWords: number): string {
    const parsed = new DOMParser().parseFromString(answer ?? '', 'text/html');
    const body = parsed.body;
    const textNodes = parsed.createTreeWalker(body, NodeFilter.SHOW_TEXT);
    let remaining = Math.max(maxWords, 0);
    let node = textNodes.nextNode() as Text | null;
    while (node) {
      const words = ContentHelper.countPlainWords(node.data);
      if (words <= remaining) {
        remaining -= words;
        node = textNodes.nextNode() as Text | null;
        continue;
      }
      node.data = ContentHelper.firstPlainWords(node.data, remaining);
      ContentHelper.removeNodesAfter(body, node);
      break;
    }
    return body.innerHTML;
  }

  static truncatePlainTextToWords(text: string | null | undefined, maxWords: number): string {
    const source = text ?? '';
    if (ContentHelper.countFormAnswerWords(source) <= maxWords) {
      return source;
    }
    let words = Math.max(maxWords, 0);
    let result = ContentHelper.firstPlainWords(source, words);
    while (words > 0 && ContentHelper.countFormAnswerWords(result) > maxWords) {
      result = ContentHelper.firstPlainWords(source, --words);
    }
    return result;
  }

  /** Java's `\s` plus NBSP, as characters inside a class - see `countFormAnswerWords`. */
  private static readonly FORM_ANSWER_WHITESPACE = ' \\t\\n\\r\\f\\u000B\\u00A0';

  private static countPlainWords(text: string): number {
    return text.split(new RegExp(`[${ContentHelper.FORM_ANSWER_WHITESPACE}]+`)).filter((word) => word.length > 0).length;
  }

  /** The first `count` words of `text` together with the whitespace that follows them. */
  private static firstPlainWords(text: string, count: number): string {
    const whitespace = ContentHelper.FORM_ANSWER_WHITESPACE;
    const match = new RegExp(`^(?:[${whitespace}]*[^${whitespace}]+){${count}}[${whitespace}]*`).exec(text);
    return match ? match[0] : '';
  }

  /** Removes every node after `node` in document order, then `node` itself and its ancestors if they are left empty. */
  private static removeNodesAfter(root: Node, node: Text): void {
    let current: Node = node;
    while (current !== root) {
      while (current.nextSibling) {
        current.nextSibling.remove();
      }
      current = current.parentNode ?? root;
    }

    let trailing: Node = node;
    while (trailing !== root && trailing.parentNode) {
      const isEmpty =
        trailing.nodeType === Node.TEXT_NODE ? ContentHelper.countPlainWords((trailing as Text).data) === 0 : !trailing.hasChildNodes();
      if (!isEmpty) {
        break;
      }
      const parent = trailing.parentNode;
      parent.removeChild(trailing);
      trailing = parent;
    }
  }

  static escapeHtml(htmlString: string): string {
    return htmlString.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  }

  static unescapeHtml(htmlString: string): string {
    // `&amp;` goes last, or an escaped entity the author typed (`&amp;lt;`) would be decoded twice.
    return htmlString
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#0?39;/g, "'")
      .replace(/&amp;/g, '&');
  }

  /**
   * Flattens authored rich text to a single line of plain text. Use wherever server content (a card `blurb`,
   * a `subHeader`) reaches a template through interpolation rather than `[innerHTML]` — without it the markup
   * shows up literally as `<p>…</p>`. Strips before unescaping, so entities the author typed survive as text.
   */
  static toPlainText(htmlContent: string | null | undefined): string {
    if (!htmlContent) {
      return '';
    }
    // `stripHtmlTags` emits a space per tag AND per whitespace run, so a tag next to a space yields two —
    // collapse once more or `an <b>x</b> on` comes out double-spaced.
    return ContentHelper.unescapeHtml(ContentHelper.stripHtmlTags(htmlContent)).replace(/\s+/g, ' ').trim();
  }

  /** Returns the first non-empty line of text found in the editor content, truncated to `wordLimit` words. */
  static getTextPreviewFromEditorContent(content: EditorContent[], wordLimit = 8): string {
    if (!content?.length) {
      return '';
    }

    for (const block of content) {
      if (typeof block.content !== 'string') {
        continue;
      }

      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = block.content;
      const text = tempDiv.textContent?.trim();

      if (text) {
        const words = text.split(/\s+/);
        return words.length > wordLimit ? `${words.slice(0, wordLimit).join(' ')}…` : text;
      }
    }

    return '';
  }
}

export function canTriggerSearch(event: KeyboardEvent): boolean {
  /* eslint-disable */
  return event.which < 9 || event.which > 40 || event.which === 32;
  /* eslint-enable */
}
